import type { AuthResponseDto, RegisterDto } from "@stylingcv/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useAuthStore } from "@/client/stores/auth";
import { useTokenStore } from "@/client/stores/token";

export const register = async (data: RegisterDto) => {
  // Debug logging

  try {
    const response = await axios.post<AuthResponseDto, AxiosResponse<AuthResponseDto>, RegisterDto>(
      "/auth/register",
      data,
    );

    return response.data;
  } catch (error: any) {
    console.error("❌ Registration failed:", error);
    console.error("📊 Error status:", error?.response?.status);
    console.error("📊 Error response:", error?.response?.data);
    console.error("📋 Error message:", error?.message);
    console.error("📋 Full error object:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
    throw error;
  }
};

export const useRegister = () => {
  const setUser = useAuthStore((state) => state.setUser);

  const {
    error,
    isPending: loading,
    mutateAsync: registerFn,
  } = useMutation({
    mutationFn: register,
    onSuccess: async (data) => {
      // Set the user data immediately from registration response
      setUser(data.user);
      queryClient.setQueryData(["user"], data.user);
      useTokenStore.getState().reset();

      // Force refetch of user data to get the latest subscription tier
      try {
        await queryClient.invalidateQueries({ queryKey: ["user"] });
      } catch {
        // Silently handle refetch errors - registration should still succeed
      }
    },
  });

  // Enhanced register function that returns parsed errors
  const registerWithErrorParsing = async (
    data: RegisterDto,
  ): Promise<{ success: boolean; fieldErrors?: Record<string, string>; user?: any }> => {
    try {
      const result = await registerFn(data);
      return { success: true, user: result.user };
    } catch (error: any) {
      console.error("💥 registerFn threw error:", error);
      console.error("📊 Error details:", {
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      
      const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";

      // Parse specific field errors
      const fieldErrors: Record<string, string> = {};

      if (errorMessage.includes("email address already exists")) {
        fieldErrors.email =
          "This email address is already in use. Please use a different email or try signing in.";
      }

      if (errorMessage.includes("username is already taken")) {
        fieldErrors.username =
          "This username is already taken. Please choose a different username.";
      }

      return { success: false, fieldErrors } as any;
    }
  };

  return { register: registerFn, registerWithErrorParsing, loading, error };
};
